import { Box, MenuItem, Select, Typography } from '@mui/material';
import moment from 'moment';
import { useState } from 'react';
import { IAvailableAppointments } from '../context/cambiapp.context';
import { CommonButton } from './button/common-button';

interface IAppointmentPicker {
	/** Lo que devuelve el calendario: [{ date, hours: [{ shift, available }] }]. */
	appointments: any[];
	selectedDates: IAvailableAppointments[];
	setSelectedDates: (dates: IAvailableAppointments[]) => void;
}

const selectSx = {
	backgroundColor: '#000000',
	padding: '6px 10px',
	color: 'white',
	minWidth: '90px',
};

const nombreDelDia = (date: string) =>
	moment(date).add(3, 'hours').format('dddd D [de] MMMM').toUpperCase();

const esLaMisma = (a: IAvailableAppointments, b: IAvailableAppointments) =>
	a.date === b.date && a.shift === b.shift;

/**
 * Elección de las citas posibles de una publicación: de a una, por tramos
 * (un día, desde y hasta), el día entero o todas. Solo se ofrecen los
 * horarios libres que da el calendario de atención.
 */
export const AppointmentPicker = ({
	appointments,
	selectedDates,
	setSelectedDates,
}: IAppointmentPicker) => {
	const [tramoDia, setTramoDia] = useState('');
	const [tramoDesde, setTramoDesde] = useState('');
	const [tramoHasta, setTramoHasta] = useState('');
	const [aviso, setAviso] = useState('');

	const dias = (appointments || []).filter((a) =>
		(a.hours || []).some((h: any) => h.available),
	);
	const libresDe = (a: any): IAvailableAppointments[] =>
		(a.hours || [])
			.filter((h: any) => h.available)
			.map((h: any) => ({ date: a.date, shift: h.shift }));
	const todas = dias.flatMap(libresDe);
	const estaElegida = (c: IAvailableAppointments) =>
		selectedDates.some((d) => esLaMisma(d, c));

	const sumar = (nuevas: IAvailableAppointments[]) =>
		setSelectedDates([
			...selectedDates,
			...nuevas.filter((c) => !estaElegida(c)),
		]);
	const quitar = (fuera: IAvailableAppointments[]) =>
		setSelectedDates(
			selectedDates.filter((d) => !fuera.some((c) => esLaMisma(d, c))),
		);

	const alternar = (c: IAvailableAppointments) =>
		estaElegida(c) ? quitar([c]) : sumar([c]);

	const todasElegidas =
		todas.length > 0 && todas.every((c) => estaElegida(c));

	const diaDelTramo = dias.find((a) => a.date === tramoDia);
	const horasDelDia: string[] = (diaDelTramo?.hours || []).map(
		(h: any) => h.shift,
	);

	const agregarTramo = () => {
		if (!diaDelTramo || !tramoDesde || !tramoHasta) return;
		const enLapso = diaDelTramo.hours.filter(
			(h: any) => h.shift >= tramoDesde && h.shift <= tramoHasta,
		);
		const libres = enLapso
			.filter((h: any) => h.available)
			.map((h: any) => ({ date: diaDelTramo.date, shift: h.shift }));
		sumar(libres);
		const salteados = enLapso.length - libres.length;
		setAviso(
			`Se agregaron ${libres.length} horarios de ${tramoDesde} a ${tramoHasta}.` +
				(salteados
					? salteados === 1
						? ' Se salteó 1 que no está disponible.'
						: ` Se saltearon ${salteados} que no están disponibles.`
					: ''),
		);
		setTramoDesde('');
		setTramoHasta('');
	};

	// Lo elegido, agrupado en tramos seguidos por día, para que se lea de un vistazo.
	const tramos: { date: string; from: string; to: string }[] = [];
	for (const dia of dias) {
		let actual: { date: string; from: string; to: string } | null = null;
		for (const h of dia.hours || []) {
			const elegida =
				h.available && estaElegida({ date: dia.date, shift: h.shift });
			if (elegida) {
				if (actual) actual.to = h.shift;
				else actual = { date: dia.date, from: h.shift, to: h.shift };
			} else if (actual && h.available) {
				tramos.push(actual);
				actual = null;
			}
		}
		if (actual) tramos.push(actual);
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
			<Box
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					marginBottom: '16px',
				}}
			>
				<CommonButton
					action={() => (todasElegidas ? setSelectedDates([]) : sumar(todas))}
					sxBgcolor={'#666666'}
				>
					<Typography fontSize={12} color={'white'} fontWeight={600}>
						{todasElegidas ? 'Quitar todas' : 'Seleccionar todas'}
					</Typography>
				</CommonButton>
			</Box>

			<Box
				sx={{
					border: '1px solid #444444',
					borderRadius: '6px',
					padding: '12px',
					marginBottom: '20px',
				}}
			>
				<Typography sx={{ fontWeight: 700, marginBottom: '8px' }}>
					Agregar un tramo
				</Typography>
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						gap: '8px',
						alignItems: 'center',
					}}
				>
					<Select
						value={tramoDia}
						displayEmpty
						variant="standard"
						sx={{ ...selectSx, minWidth: '170px' }}
						onChange={(e) => {
							setTramoDia(e.target.value as string);
							setTramoDesde('');
							setTramoHasta('');
						}}
					>
						<MenuItem value="" sx={{ display: 'none' }}>
							Día
						</MenuItem>
						{dias.map((a) => (
							<MenuItem key={a.date} value={a.date}>
								{nombreDelDia(a.date)}
							</MenuItem>
						))}
					</Select>
					<Select
						value={tramoDesde}
						displayEmpty
						disabled={!tramoDia}
						variant="standard"
						sx={selectSx}
						onChange={(e) => {
							const v = e.target.value as string;
							setTramoDesde(v);
							if (tramoHasta && tramoHasta < v) setTramoHasta('');
						}}
					>
						<MenuItem value="" sx={{ display: 'none' }}>
							Desde
						</MenuItem>
						{horasDelDia.map((h) => (
							<MenuItem key={h} value={h}>
								{h}
							</MenuItem>
						))}
					</Select>
					<Select
						value={tramoHasta}
						displayEmpty
						disabled={!tramoDesde}
						variant="standard"
						sx={selectSx}
						onChange={(e) => setTramoHasta(e.target.value as string)}
					>
						<MenuItem value="" sx={{ display: 'none' }}>
							Hasta
						</MenuItem>
						{horasDelDia
							.filter((h) => h >= tramoDesde)
							.map((h) => (
								<MenuItem key={h} value={h}>
									{h}
								</MenuItem>
							))}
					</Select>
					<CommonButton
						action={agregarTramo}
						disabled={!tramoDia || !tramoDesde || !tramoHasta}
						sxBgcolor={'#405F8E'}
					>
						<Typography fontSize={12} color={'white'} fontWeight={600}>
							Agregar tramo
						</Typography>
					</CommonButton>
				</Box>
				{aviso && (
					<Typography sx={{ fontSize: 13, color: '#BFBFBF', marginTop: '8px' }}>
						{aviso}
					</Typography>
				)}
			</Box>

			{tramos.length > 0 && (
				<Typography sx={{ fontSize: 14, marginBottom: '20px' }}>
					<b>Elegiste:</b>{' '}
					{tramos
						.map(
							(t) =>
								`${moment(t.date).add(3, 'hours').format('ddd D/M')} ${
									t.from === t.to ? t.from : `de ${t.from} a ${t.to}`
								}`,
						)
						.join(' · ')}
				</Typography>
			)}

			{dias.map((appointment) => {
				const delDia = libresDe(appointment);
				const diaCompleto = delDia.every((c) => estaElegida(c));
				return (
					<Box
						key={appointment.date}
						sx={{
							display: 'flex',
							flexDirection: 'column',
							alignItems: 'center',
							marginBottom: '25px',
						}}
					>
						<Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
							<Typography>{nombreDelDia(appointment.date)}</Typography>
							<Typography
								onClick={() => (diaCompleto ? quitar(delDia) : sumar(delDia))}
								sx={{
									fontSize: 12,
									fontWeight: 600,
									color: '#7FB2FF',
									cursor: 'pointer',
									textDecoration: 'underline',
								}}
							>
								{diaCompleto ? 'Quitar el día' : 'Todo el día'}
							</Typography>
						</Box>
						<Box
							sx={{
								display: 'flex',
								flexWrap: 'wrap',
								justifyContent: 'center',
							}}
						>
							{delDia.map((c) => (
								<Typography
									key={`${c.date}${c.shift}`}
									onClick={() => alternar(c)}
									sx={{
										display: 'inline-block',
										bgcolor: estaElegida(c) ? '#3C8846' : '#405F8E',
										margin: '2px',
										padding: '1px',
										cursor: 'pointer',
									}}
								>
									{c.shift}
								</Typography>
							))}
						</Box>
					</Box>
				);
			})}
		</Box>
	);
};
